import ExcelJS from 'exceljs';

/**
 * EU-551/EU-552 — helpers de XLSX dos wizards de movimentação/reprovação:
 * parse da base de IDs, gabarito ("Baixar modelo") e base de conflitos.
 * Reusa o `exceljs` já presente no recruiter (mesmo dep do parser de vagas
 * e do export de respostas).
 */

export const BULK_XLSX_MAX_BYTES = 5 * 1024 * 1024;

export const BULK_ERR_FORMAT = 'Formato não aceito. Envie seu arquivo em .XLSX.';
export const BULK_ERR_SIZE = 'O arquivo excede o limite de 5MB. Por favor, envie uma versão menor.';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export class BulkIdsParseError extends Error {
  readonly name = 'BulkIdsParseError';
}

export interface ParsedIdsBase {
  /** IDs bem-formados (uuid), deduplicados, na ordem da planilha. */
  wellFormed: string[];
  /** Tokens não-uuid (viram "ID inválidos" no wizard, sem ir ao servidor). */
  malformed: string[];
}

function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    const v = value as { text?: unknown; result?: unknown; richText?: { text?: string }[] };
    if (typeof v.text === 'string') return v.text.trim();
    if (typeof v.result === 'string') return v.result.trim();
    if (Array.isArray(v.richText))
      return v.richText
        .map((r) => r.text ?? '')
        .join('')
        .trim();
  }
  return '';
}

/** Validação local do arquivo (RN de formato/tamanho) ANTES de qualquer parse. */
export function validateBulkFile(file: File): string | null {
  const isXlsx =
    /\.xlsx$/i.test(file.name) ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (!isXlsx) return BULK_ERR_FORMAT;
  if (file.size > BULK_XLSX_MAX_BYTES) return BULK_ERR_SIZE;
  return null;
}

/**
 * Lê a coluna A da primeira aba: cada célula é um ID. A primeira linha
 * não-uuid é tolerada como cabeçalho do gabarito (não conta como inválido);
 * qualquer outro token não-uuid vira `malformed` ("ID inválidos" no resumo —
 * classificado LOCALMENTE, sem ir ao servidor).
 */
export async function parseIdsXlsx(file: File): Promise<ParsedIdsBase> {
  const workbook = new ExcelJS.Workbook();
  try {
    await workbook.xlsx.load(await file.arrayBuffer());
  } catch {
    throw new BulkIdsParseError(BULK_ERR_FORMAT);
  }
  const sheet = workbook.worksheets[0];
  if (!sheet) throw new BulkIdsParseError(BULK_ERR_FORMAT);

  const wellFormed: string[] = [];
  const malformed: string[] = [];
  const seen = new Set<string>();

  sheet.eachRow((row, rowNumber) => {
    const raw = cellText(row.getCell(1).value);
    if (!raw) return;
    if (UUID_RE.test(raw)) {
      const id = raw.toLowerCase();
      if (!seen.has(id)) {
        seen.add(id);
        wellFormed.push(id);
      }
      return;
    }
    // Cabeçalho do gabarito na linha 1 — ignorado silenciosamente.
    if (rowNumber === 1) return;
    malformed.push(raw);
  });

  return { wellFormed, malformed };
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** "Baixar modelo" — gabarito com uma coluna de IDs. */
export async function downloadIdsTemplateXlsx(): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Base');
  sheet.addRow(['ID do candidato']);
  sheet.getRow(1).font = { bold: true };
  sheet.columns.forEach((col) => {
    col.width = 40;
  });
  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    'modelo-base-candidatos.xlsx'
  );
}

export interface ConflictRow {
  id: string;
  name: string | null;
  email: string | null;
  /** "Desistiu da vaga" / "Já movimentado" / "Já reprovado" / "ID inválido". */
  reason: string;
}

/**
 * "Baixar base de conflitos" — XLSX com ID, nome, e-mail e motivo (RN-09 mov /
 * RN-05 rep). Para IDs inválidos, nome/e-mail ficam vazios (só ID + motivo).
 */
export async function downloadConflictsXlsx(rows: ConflictRow[], fileName: string): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Conflitos');
  sheet.addRow(['ID', 'Nome', 'E-mail', 'Motivo']);
  sheet.getRow(1).font = { bold: true };
  for (const row of rows) {
    sheet.addRow([row.id, row.name ?? '', row.email ?? '', row.reason]);
  }
  sheet.columns.forEach((col) => {
    col.width = 40;
  });
  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    fileName
  );
}
